import { themeTokens, type ThemeTokens } from '@/previewThemes/meta';
import type { ThemeTokensOverride } from '@/features/storefront/themeSchema';

/**
 * A layout family's design constants with a built theme's overrides laid over
 * them.
 *
 * A theme built from screenshots on the platform panel is data on top of a
 * shipped family: it borrows the family's header, footer and product page but
 * brings its own accent, spacing and heading voice. Those arrive as the
 * document's `tokens` (snake_case, every field nullable) and are laid over the
 * family here - field by field, `null` meaning "the family's own" - so an
 * ordinary document, which has none, gets exactly `themeTokens(family)`.
 */
export function overrideTokens(
  base: ThemeTokens,
  override: ThemeTokensOverride | null | undefined,
): ThemeTokens {
  if (!override) return base;
  const tokens: ThemeTokens = { ...base };
  if (override.accent != null) tokens.accent = override.accent;
  if (override.mono_font != null) tokens.monoFont = override.mono_font;
  if (override.display_font != null) tokens.displayFont = override.display_font;
  if (override.heading_tracking != null) tokens.headingTracking = override.heading_tracking;
  if (override.heading_transform != null) tokens.headingTransform = override.heading_transform;
  if (override.heading_align != null) tokens.headingAlign = override.heading_align;
  if (override.hero_edge != null) tokens.heroEdge = override.hero_edge;
  if (override.card_ratings != null) tokens.cardRatings = override.card_ratings;
  if (override.band_frame != null) tokens.bandFrame = override.band_frame;
  if (override.container_max != null) tokens.containerMax = override.container_max;
  if (override.space_scale != null) tokens.spaceScale = override.space_scale;
  if (override.header_height != null) tokens.headerHeight = override.header_height;
  return tokens;
}

/**
 * The tokens a theme document renders with: its family's, under its own
 * overrides. Every screen that paints a live or preview shop from a document
 * reads tokens through this rather than `themeTokens(layout.family)`, so a
 * built theme looks the same in the preview, the customiser and the shop.
 *
 * `family` overrides which family's tokens are the base - the header can be
 * drawn by another family, and it is that family's height a banner subtracts.
 */
export function effectiveTokens(
  theme: { layout: { family: string }; tokens?: ThemeTokensOverride | null },
  family: string = theme.layout.family,
): ThemeTokens {
  return overrideTokens(themeTokens(family), theme.tokens);
}
